// Lazily-loaded OU/account tree with checkboxes, for the StackSets target picker.
//
// Same rows, icons and chips as OrgTreeCheckbox. OUs and accounts are both
// selectable here (suspended accounts are not — AWS will not deploy to them),
// and one difference drives everything else: expansion is CONTROLLED by the page's
// useLazyOrgTree cache instead of local state, because a level does not exist
// until it is fetched. Every ROOT/OU row therefore gets a chevron
// unconditionally — asking whether a node has children in advance would cost the
// extra round trip per child that made the whole-tree load time out.
//
// Exclusions (AWS account-filter DIFFERENCE): when the page passes
// onToggleExclude, everything under a selected OU renders as included by
// inheritance. An inherited ACCOUNT row toggles an exclusion (unticked, struck
// through, "Excluded" chip); an inherited child OU is ticked and disabled
// ("included via parent") — excluding whole sub-OUs is out of scope, AWS has no
// OU-level DIFFERENCE either. Without onToggleExclude the tree behaves exactly
// as before.
//
// selectionMode="single" is a radio-style OU picker: OU/ROOT rows only (accounts
// are hidden), one selection, reported through onToggleSelect.
//
// OrgTreeCheckbox is left alone: the admin Policies picker is flat and needs a
// materialised tree, so one component cannot serve both without carrying two
// behaviours.
import { Box, Button, Checkbox, Chip, CircularProgress, Collapse, IconButton, Radio, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/FolderOpen';
import CloudIcon from '@mui/icons-material/Cloud';
import { CK, FONT_BODY, statusChipSx } from '../../pages/portal/portalStyles';

const treeChipSx = (intent) => ({
  ...statusChipSx(intent),
  ml: 0.5,
  height: 20,
  fontSize: '0.65rem',
  flexShrink: 0,
});

const captionSx = {
  ml: 0.75, fontFamily: FONT_BODY, fontSize: '0.72rem', color: CK.muted, flexShrink: 0, fontStyle: 'italic',
};

const LazyOrgTreeCheckbox = ({
  node, depth = 0, tree, selectedIds = [], onToggleSelect,
  // Exclusions: inheritedFrom is the name of the selected ancestor OU (set by
  // the recursion, not by pages); excludedIds lists excluded account ids.
  inheritedFrom = null, excludedIds = [], onToggleExclude,
  // Optional label for the tenant root row (e.g. "All accounts in the organization").
  rootLabel,
  selectionMode = 'multiple',
}) => {
  // The API returns both 'OU' and 'ORGANIZATIONAL_UNIT' for the same kind of node.
  const kind = node.type === 'ORGANIZATIONAL_UNIT' ? 'OU' : node.type;
  const single = selectionMode === 'single';
  const canExpand = kind === 'ROOT' || kind === 'OU';
  const expanded = canExpand && Boolean(tree.expanded[node.id]);
  const rawChildren = tree.children[node.id];
  // The single-select OU picker never shows accounts.
  const children = single && rawChildren
    ? rawChildren.filter((c) => c.type !== 'ACCOUNT')
    : rawChildren;
  const loading = Boolean(tree.loading[node.id]);
  const error = tree.errors[node.id];
  const isSelected = selectedIds.some((n) => n.id === node.id);
  const suspended = kind === 'ACCOUNT' && Boolean(node.status) && node.status !== 'ACTIVE';

  // Inheritance only exists when the page opted into exclusions.
  const exclusionsOn = !single && typeof onToggleExclude === 'function';
  const inherited = exclusionsOn && Boolean(inheritedFrom) && !isSelected;
  const isExcluded = inherited && kind === 'ACCOUNT' && excludedIds.includes(node.id);
  // What this node passes to its children: the nearest selected OU's name.
  const childInheritedFrom = exclusionsOn
    ? (inheritedFrom || (isSelected && canExpand ? (kind === 'ROOT' && rootLabel ? rootLabel : node.name) : null))
    : null;

  const label = kind === 'ROOT' && rootLabel ? rootLabel : node.name;

  const getIcon = () => {
    switch (kind) {
      case 'ROOT': return <BusinessIcon sx={{ fontSize: '1rem', color: CK.blue, flexShrink: 0 }} />;
      case 'OU': return <FolderIcon sx={{ fontSize: '1rem', color: CK.amber, flexShrink: 0 }} />;
      case 'ACCOUNT': return <CloudIcon sx={{ fontSize: '1rem', color: CK.green, flexShrink: 0 }} />;
      default: return null;
    }
  };

  const selectPayload = { id: node.id, name: node.name, type: node.type };

  let control;
  if (single) {
    control = (
      <Radio
        size="small"
        checked={isSelected}
        onChange={() => onToggleSelect(selectPayload)}
        inputProps={{ 'aria-label': label }}
        sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
      />
    );
  } else if (inherited && kind === 'ACCOUNT') {
    control = (
      <Checkbox
        size="small"
        checked={!isExcluded}
        disabled={suspended}
        onChange={() => onToggleExclude(selectPayload)}
        inputProps={{ 'aria-label': `${isExcluded ? 'Include' : 'Exclude'} ${node.name}` }}
        sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
      />
    );
  } else if (inherited) {
    // A child OU of a selected OU: covered by its parent, not separately excludable.
    control = (
      <Checkbox
        size="small"
        checked
        disabled
        inputProps={{ 'aria-label': `${node.name} (included via parent)` }}
        sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
      />
    );
  } else {
    control = (
      <Checkbox
        size="small"
        checked={isSelected}
        // Accounts are targetable in their own right; suspended ones are not —
        // AWS will not deploy to them and the count skips them, so offering the
        // checkbox would promise something that cannot happen.
        disabled={suspended}
        // The RAW node.type, not the normalised `kind`: the page routes the
        // selection into targetOus vs targetAccounts and needs to tell an
        // unrecognised kind from a real one, which collapsing OU spellings here
        // would hide. Consumers must match kinds explicitly and ignore what they
        // do not recognise — never route the leftovers into OUs by default.
        onChange={() => onToggleSelect(selectPayload)}
        inputProps={{ 'aria-label': node.name }}
        sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
      />
    );
  }

  return (
    <Box sx={{ ml: depth > 0 ? 3 : 0 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', py: 0.5, px: 0.5, minWidth: 0,
          borderRadius: '6px', transition: 'background-color 0.2s ease',
          '&:hover': { backgroundColor: CK.hover },
        }}
      >
        {canExpand ? (
          <IconButton
            size="small"
            onClick={() => tree.toggle(node)}
            // Named per row: a tree of OUs whose every chevron announces only
            // "Expand" tells a screen-reader user nothing. Matches the
            // convention ResourceTree already uses in portalStyles.js.
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
            sx={{ mr: 0.5, p: 0.25, flexShrink: 0 }}
          >
            {loading ? (
              <CircularProgress size={14} sx={{ color: CK.amber }} />
            ) : (
              <ExpandMoreIcon
                sx={{
                  fontSize: '1rem',
                  transform: expanded ? 'none' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, mr: 0.5, flexShrink: 0 }} />
        )}
        {control}
        {getIcon()}
        <Typography
          noWrap
          sx={{
            ml: 0.5, fontFamily: FONT_BODY, fontSize: '0.85rem', color: isExcluded ? CK.muted : CK.navy,
            textDecoration: isExcluded ? 'line-through' : 'none',
            minWidth: 0, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        {kind === 'ACCOUNT' && node.id && (
          <Typography
            noWrap
            sx={{
              ml: 0.5, fontFamily: 'monospace', fontSize: '0.75rem', color: CK.muted, flexShrink: 0,
              textDecoration: isExcluded ? 'line-through' : 'none',
            }}
          >
            {node.id}
          </Typography>
        )}
        <Chip
          label={kind}
          size="small"
          sx={treeChipSx(kind === 'ACCOUNT' ? 'success' : kind === 'OU' ? 'warning' : 'info')}
        />
        {node.status && node.status !== 'ACTIVE' && (
          <Chip label={node.status} size="small" sx={treeChipSx('error')} />
        )}
        {isExcluded && <Chip label="Excluded" size="small" sx={treeChipSx('error')} />}
        {inherited && kind === 'ACCOUNT' && !isExcluded && (
          <Typography noWrap sx={captionSx}>via {inheritedFrom}</Typography>
        )}
        {inherited && kind !== 'ACCOUNT' && (
          <Typography noWrap sx={captionSx}>included via parent</Typography>
        )}
      </Box>

      {canExpand && (
        <Collapse in={expanded}>
          {error ? (
            <Box sx={{ ml: 3, py: 0.75, px: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.red }}>
                {error}
              </Typography>
              <Button
                size="small"
                onClick={() => tree.retry(node.id)}
                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, p: 0.25 }}
              >
                Try again
              </Button>
            </Box>
          ) : !children ? (
            <Box sx={{ ml: 3, py: 0.75, px: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={12} sx={{ color: CK.amber }} />
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted }}>
                Loading…
              </Typography>
            </Box>
          ) : children.length === 0 ? (
            <Typography sx={{ ml: 3, py: 0.75, px: 0.5, fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted }}>
              {single && rawChildren && rawChildren.length > 0
                ? 'No organizational units below this one.'
                : 'This organizational unit is empty.'}
            </Typography>
          ) : (
            children.map((child) => (
              <LazyOrgTreeCheckbox
                key={child.id}
                node={child}
                depth={depth + 1}
                tree={tree}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                inheritedFrom={childInheritedFrom}
                excludedIds={excludedIds}
                onToggleExclude={onToggleExclude}
                selectionMode={selectionMode}
              />
            ))
          )}
        </Collapse>
      )}
    </Box>
  );
};

export default LazyOrgTreeCheckbox;
